/**
 * ============================================================================
 * GUIDED TOUR — MOBILE MENU BRIDGE (Phase 4b fixes)
 * ============================================================================
 * On viewports < lg (1024 px), the Tool page renders its sidebar inside the
 * mobile menu overlay (`PlatformSidebar` only mounts when `isMobileMenuOpen`
 * is true). The two tour anchors hosted on the sidebar —
 * `[data-tour="platform-sidebar"]` and `[data-tour="nav-dashboard"]` — are
 * therefore missing on mobile until the menu is opened. The TourProvider's
 * DOM guard runs once at start and would drop both steps silently.
 *
 * Mirrors `tourNavigation.ts`: Tool.tsx registers its `setIsMobileMenuOpen`
 * setter once on mount. Tour entry points wrap `startTour` with
 * `startTourWithMobileMenu` so the sidebar is in the DOM before the DOM guard
 * runs. The two specific steps also use `onHighlightStarted` as a defensive
 * re-open in case the menu was somehow dismissed mid-tour.
 * ============================================================================
 */

type MobileMenuOpener = (open: boolean) => void;

let openerRef: MobileMenuOpener | null = null;

/** Tool.tsx registers/clears its setter on mount/unmount. */
export function setMobileMenuOpener(fn: MobileMenuOpener | null): void {
  openerRef = fn;
}

/**
 * Open the Tool-page mobile menu if running on a < lg viewport. Returns true
 * if it actually opened the menu (i.e. mobile + opener registered + not
 * already open is not checked — duplicate opens are harmless). The boolean
 * lets callers decide whether to wait a frame for the mount to commit.
 */
export function openMobileMenuForTour(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth >= 1024) return false; // desktop / `lg:` breakpoint
  if (!openerRef) return false;
  openerRef(true);
  return true;
}

/**
 * Run `start` (`tour.startTour(phase)`) with the mobile menu already mounted
 * so the DOM guard inside TourProvider sees sidebar-hosted tour anchors.
 *
 * On desktop or pages without a registered opener, runs `start` synchronously.
 * On mobile with an opener, opens the menu and defers `start` by ~50 ms so
 * React commits the sidebar mount before TourProvider builds the driver
 * instance.
 */
export function startTourWithMobileMenu(start: () => void): void {
  if (openMobileMenuForTour()) {
    setTimeout(start, 50);
  } else {
    start();
  }
}
